import { BankAccountDetails } from '@40seas/entities/bank-account';
import { datadogRumProvider } from '@40seas/frontend/libs/providers';
import { Address } from '@40seas/models';
import { copyToClipboard } from '@40seas/payments-shared';
import { BankConnectivityProvidersService } from '@40seas/shared/bank-connectivity-providers-service';
import { Box, Button, Paper, styled, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from '@mui/material';
import { useState } from 'react';
import { useNotify } from 'react-admin';

export const BankDetails = ({
    bankAccountDetails,
    reference,
    externalId,
    isOneOfMany,
}: {
    bankAccountDetails?: BankAccountDetails;
    reference?: string;
    externalId?: string;
    isOneOfMany?: boolean;
}) => {
    const notify = useNotify();
    const keys = BankConnectivityProvidersService.getVirtualAccountFields(bankAccountDetails?.currency);
    const displayedReference = reference ?? externalId;

    const [detailsCopied, setDetailsCopied] = useState(false);

    const copyDetails = async () => {
        const accountSchemeDetails = keys
            .filter((key) => bankAccountDetails?.bankAccountPayload?.[key])
            .map((key) => {
                return `${BankConnectivityProvidersService.formatBankAccountField(key)}: ${bankAccountDetails?.bankAccountPayload?.[key]}`;
            })
            .join('\n');

        const bankName = `Bank name: ${bankAccountDetails?.bankName}`;
        const accountName = bankAccountDetails?.bankAccountName ? `Account Name: ${bankAccountDetails?.bankAccountName}` : '';
        const bankAddress = `Bank address: ${addressDescription(bankAccountDetails?.bankAddress)}`;

        const reference = displayedReference ? `\nReference Code: ${displayedReference}` : '';

        const details = `Here are the ${bankAccountDetails?.currency} account details for payment:\n${bankName}\n${accountName}\n${accountSchemeDetails}\n${bankAddress}${reference}`;

        try {
            await copyToClipboard(Promise.resolve(details));
            setDetailsCopied(true);
        } catch (error: any) {
            notify('Failed to copy details to clipboard');
            datadogRumProvider.reportToDatadog(error);
        }
    };

    if (!bankAccountDetails) {
        return null;
    }

    const bankName = bankAccountDetails?.bankName;
    const accountName = bankAccountDetails?.bankAccountName;
    const bankAddress = addressDescription(bankAccountDetails?.bankAddress);

    return (
        <StyledBankDetails>
            <>
                {!isOneOfMany && (
                    <TableContainer component={Paper} sx={{ boxShadow: 'none', border: 'none' }}>
                        <Table sx={{ borderCollapse: 'collapse' }}>
                            <TableBody>
                                <TableRow key="bankName" sx={{ height: 'auto' }}>
                                    <TableCell sx={{ border: 'none', padding: 0 }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ marginBottom: '2px' }}>
                                            Bank Name
                                        </Typography>
                                        <Typography variant="body2" sx={{ marginBottom: '2px' }}>
                                            {bankName}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button variant="outlined" size="small" onClick={copyDetails}>
                                            {detailsCopied ? 'copied!' : 'Copy details'}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                {accountName &&
                    (!isOneOfMany ? (
                        <Box key={'bankAccountName'} className="BankDetail">
                            <Typography variant="body2" color="text.secondary">
                                Account name
                            </Typography>
                            <Typography variant="body2">{accountName}</Typography>
                        </Box>
                    ) : (
                        <Table sx={{ borderCollapse: 'collapse' }}>
                            <TableBody>
                                <TableRow key="bankName" sx={{ height: 'auto' }}>
                                    <TableCell sx={{ border: 'none', padding: 0 }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ marginBottom: '2px' }}>
                                            Account name
                                        </Typography>
                                        <Typography variant="body2">{accountName}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button variant="outlined" size="small" onClick={copyDetails}>
                                            {detailsCopied ? 'copied!' : 'Copy details'}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    ))}
                {keys.map((key) => {
                    if (bankAccountDetails?.bankAccountPayload?.[key]) {
                        return (
                            <Box key={key} className="BankDetail">
                                <Typography variant="body2" color="text.secondary">
                                    {BankConnectivityProvidersService.formatBankAccountField(key)}
                                </Typography>
                                <Typography variant="body2">{bankAccountDetails?.bankAccountPayload?.[key]}</Typography>
                            </Box>
                        );
                    }

                    return null;
                })}
                <Box className="BankDetail">
                    <Typography variant="body2" color="text.secondary">
                        Bank address
                    </Typography>
                    <Typography variant="body2">{bankAddress}</Typography>
                </Box>

                {displayedReference && (
                    <Box key="reference" className="BankDetail">
                        <Typography variant="body2" color="text.secondary">
                            Reference Code
                        </Typography>
                        <Typography variant="body2">{displayedReference}</Typography>
                    </Box>
                )}
            </>
        </StyledBankDetails>
    );
};

export const StyledBankDetails = styled(Box)(({ theme }) => ({
    display: 'flex',
    borderBottomLeftRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius,
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '5px 0 10px 0',
    ['& .BankDetail']: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
    ['& .BankDetail p']: {
        textAlign: 'left',
    },
}));

function addressDescription(bankAddress: Address | undefined) {
    return bankAddress
        ? [bankAddress.addressLine1, bankAddress.city, bankAddress.state, bankAddress.zipCode, bankAddress.country]
              .filter(Boolean)
              .join(', ')
        : '';
}
